import {
  DocumentOrganizationKeys,
  FilterDocument,
  FilterDocumentDateCustomTypes,
  FilterDocumentDateFilterTypes,
  FilterOptionKeys,
  GroupByMode,
  SortingMode,
  TaskGroupBy,
  TaskType,
  TodayGroupBy,
  UserSettings,
} from '@griply/types';
import { endOfDay } from 'date-fns';

import { pickFilterOptions } from './pick-filter-options';

const todaySavedFilterKeys = [
  FilterOptionKeys.LifeArea,
  FilterOptionKeys.Goal,
  FilterOptionKeys.GoalActive,
  FilterOptionKeys.GoalImpact,
  FilterOptionKeys.Duration,
  FilterOptionKeys.Priority,
] as const;

export const buildTodayDefaultFilter = (today: Date): FilterDocument => {
  const scheduledBranch = {
    [FilterOptionKeys.Date]: {
      type: FilterDocumentDateFilterTypes.Custom,
      custom: {
        type: FilterDocumentDateCustomTypes.Before,
        start: null,
        end: endOfDay(today),
      },
    },
  };

  const deadlineBranch = {
    [FilterOptionKeys.Deadline]: {
      type: FilterDocumentDateFilterTypes.Custom,
      custom: {
        type: FilterDocumentDateCustomTypes.Before,
        start: null,
        end: endOfDay(today),
      },
    },
  };

  return {
    [DocumentOrganizationKeys.GroupByMode]: GroupByMode.Deadline as TaskGroupBy,
    [DocumentOrganizationKeys.SortingMode]: SortingMode.DueDate,
    [DocumentOrganizationKeys.Filters]: {
      [FilterOptionKeys.Type]: null,
      [FilterOptionKeys.Tag]: null,
    },
    [DocumentOrganizationKeys.Or]: [scheduledBranch, deadlineBranch],
  };
};

export const buildTodayFilterDocument = (
  today: Date,
  settings?: UserSettings,
): FilterDocument => {
  const baseDocument = buildTodayDefaultFilter(today);

  if (!settings) {
    return baseDocument;
  }

  const groupByMode =
    (settings.todayGroupBy
      ? settings.todayGroupBy === TodayGroupBy.None
        ? GroupByMode.Deadline
        : (settings.todayGroupBy as unknown as TaskGroupBy)
      : undefined) ?? baseDocument[DocumentOrganizationKeys.GroupByMode];

  const sortingMode =
    settings.todoSortingMode ??
    baseDocument[DocumentOrganizationKeys.SortingMode];

  const typeFilter = settings.todayHideHabits
    ? {
        include: Object.values(TaskType).filter(
          (type) => type !== TaskType.Habit,
        ),
      }
    : baseDocument[DocumentOrganizationKeys.Filters]?.[FilterOptionKeys.Type];

  const savedFilters =
    settings.filters?.today?.[DocumentOrganizationKeys.Filters];

  const savedTagFilter = savedFilters?.[FilterOptionKeys.Tag];

  const tagFilter =
    settings.todayFilterByTags?.length || savedTagFilter?.noTag
      ? {
          include: settings.todayFilterByTags?.length
            ? settings.todayFilterByTags
            : null,
          noTag: savedTagFilter?.noTag ?? null,
          matchMode: savedTagFilter?.matchMode ?? null,
        }
      : baseDocument[DocumentOrganizationKeys.Filters]?.[FilterOptionKeys.Tag];

  return {
    ...baseDocument,
    [DocumentOrganizationKeys.GroupByMode]: groupByMode,
    [DocumentOrganizationKeys.SortingMode]: sortingMode,
    [DocumentOrganizationKeys.Filters]: {
      ...baseDocument[DocumentOrganizationKeys.Filters],
      [FilterOptionKeys.Type]: typeFilter,
      [FilterOptionKeys.Tag]: tagFilter,
      ...pickFilterOptions(savedFilters, todaySavedFilterKeys),
    },
    [DocumentOrganizationKeys.Or]: baseDocument[DocumentOrganizationKeys.Or],
    [DocumentOrganizationKeys.CollapsedSections]:
      settings.filters?.today?.[DocumentOrganizationKeys.CollapsedSections],
  };
};
